import React from 'react';
import { useObjectivesList } from '../../../../hooks/useObjectivesList';
import {
  Divider,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import {
  DYNAMIC_VIEW_REDUCER_ACTIONS,
  useDynamicViewState,
  useDynamicViewDispatch,
} from '../../context/dynamic-view-context';
import { useContentContext } from '../../context/content-context';

/**
 * @fileoverview Componente selector de nodos para widgets del Panel Dinámico.
 * @module SelectNode
 */

/**
 * Componente SelectNode.
 *
 * Permite al usuario seleccionar un nodo específico de un objetivo monitoreado
 * para filtrar o visualizar datos asociados a ese nodo.
 *
 * Funcionalidad:
 * - Obtiene la lista de objetivos y sus nodos desde el almacenamiento o API.
 * - Filtra los nodos correspondientes al `objetivoId` del widget actual (slot).
 * - Renderiza un menú desplegable con los nodos disponibles.
 * - Al seleccionar un nodo, actualiza la propiedad `customProperties.nodoId` del widget
 *   en el estado global.
 *
 * @returns {React.ReactElement|null} Selector de nodos o null si no hay datos.
 *
 * @author David Maldonado
 * (c) Servicios de Monitoreo S.A. - Santiago, Chile
 */
export function SelectNode() {
  const { t } = useTranslation();
  const {
    data: objectives,
    isLoading: isLoadingObjectiveList,
  } = useObjectivesList({ fromStorage: true });
  const state = useDynamicViewState();
  const dispatch = useDynamicViewDispatch();
  const { slot } = useContentContext();

  /**
   * Lista de nodos asociados al objetivo actual.
   * Se obtiene buscando el objetivo por ID en la lista completa de objetivos.
   * Memorizado para evitar recálculos innecesarios.
   * @type {Array<Object>|undefined}
   */
  const nodosObjetivo = React.useMemo(() => {
    return objectives?.find((obj) => obj.objetivo_id === slot.objetivoId)
      ?.Nodos;
  }, [objectives, slot.objetivoId]);

  /**
   * Manejador para el cambio de nodo seleccionado.
   *
   * Actualiza el layout global modificando la propiedad `nodoId` en las
   * `customProperties` del widget correspondiente.
   *
   * @param {Object} event - Evento de cambio del componente Select.
   * @param {number} event.target.value - ID del nodo seleccionado.
   */
  function handleChange(event) {
    const value = event.target.value;
    const newLayout = [...state.activeLayout].map((item) => {
      if (item.id === slot.id) {
        return {
          ...item,
          customProperties: { ...(item.customProperties ?? {}), nodoId: value },
        };
      }
      return item;
    });

    dispatch({
      type: DYNAMIC_VIEW_REDUCER_ACTIONS.UPDATE_TEMPLATE,
      payload: newLayout,
    });
  }
  return (
    <>
      {nodosObjetivo ? (
        <FormControl fullWidth value={slot.customProperties?.nodoId || 0}>
          <InputLabel id="select-nodo-label">
            {t('dynamicPanel.selectNode.node')}
          </InputLabel>
          <Select
            labelId="select-nodo-label"
            disabled={isLoadingObjectiveList}
            displayEmpty
            onChange={handleChange}
            label={t('dynamicPanel.selectNode.node')}
            value={slot.customProperties?.nodoId || 0}
          >
            {nodosObjetivo.map((nodo) => (
              <MenuItem key={nodo.id_nodo} value={nodo.id_nodo}>
                {nodo.nodo}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : (
        <></>
      )}
      {isLoadingObjectiveList && <LinearProgress />}
    </>
  );
  return null;
}
